import React, { useEffect, useRef, useState } from 'react';
import { useAnnotationsQuery, useModifyAnnotationMutation, useRemoveAnnotationMutation } from '@hook/gridgio/useApi';
import LoadingDots from '@design-system/loading-dots';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/gridgio/modal-slice';
import { GRIDGIO_ISSUES } from '@constant/gridgio/issue';
import { v4 as uuid4 } from 'uuid';
import Handwriting from '@design-system/handwriting';
import styled from 'styled-components';
import IconTextBlock from '@component/common/block/icon-text-block';
import TextBlock from '@component/common/block/text-block';
import TextButton from '@design-system/text-button';
import type { IAnnotation, IAnnotationMemo, IAnnotationModalData } from '@datatype/gridgio/type';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme } from '@datatype/type';
import Gap from '@component/common/gap';

interface IProps {
  data: IAnnotationModalData['data'];
}

const Wrapper = styled.div<{ $mode: IAnnotationModalData['data']['mode'] }>`
  display: flex;
  width: ${({ $mode }) => ($mode === 'delete' ? size.MEDIUM_MODAL_WIDTH : size.GRIDGIO_LARGE_MODAL_WIDTH)};
  flex-flow: column nowrap;
  align-items: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  width: 100%;
  gap: ${size.MARGIN_1};
`;

const MemoItemsContainer = styled.div`
  display: flex;
  width: 100%;
  max-height: 420px;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_1};
  overflow-y: auto;
`;

const MemoItemContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: ${size.MARGIN_1};
`;

const MemoPreviewContainer = styled.div`
  display: flex;
  width: 240px;
  flex: 0 0 240px;
  padding: ${size.MARGIN_1};
  background-color: ${color.CYAN_5};
  border: ${size.BORDER_WIDTH_1} solid ${color.CYAN_4};
  border-radius: ${size.BORDER_RADIUS_2};
  cursor: pointer;
`;

const PropertyFieldContainer = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  flex-flow: column nowrap;
  gap: 5px;
`;

const PropertyLabelContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const PROPERTY_LABEL_DESIGN = {
  box: { alignX: 'left' as const },
  text: { size: 'medium' as const, color: color.BLACK_2, bold: true },
};

const PROPERTY_CLEAR_DESIGN = {
  box: { gap: '5px', event: { cursor: 'pointer' as const } },
  text: { size: 'small' as const, color: color.CYAN_2, oneline: true },
  icon: { size: 'small' as const, kind: 'outlined' as const, color: color.CYAN_2 },
};

const AnnotationModal: React.FC<IProps> = ({ data }) => {
  const dispatch = useTypedDispatch();
  const annotationQuery = useAnnotationsQuery(data.workKey);
  const record = annotationQuery.data?.data.find(
    (item) => item.key === data.annotationId && item.sheetId === data.sheetId,
  );
  const annotation: IAnnotation | undefined = record
    ? { id: record.key, issueId: record.issueId, mark: record.mark, property: record.property, memo: record.memo }
    : undefined;
  const annotationMutation = useModifyAnnotationMutation();
  const removeMutation = useRemoveAnnotationMutation();
  const isPending = annotationMutation.isPending || removeMutation.isPending;
  const [errorMessage, setErrorMessage] = useState('');
  const issue = GRIDGIO_ISSUES.find((item) => item.id === annotation?.issueId);
  const property = annotation?.property;
  const memo = annotation?.memo ?? [];
  const [mode, setMode] = useState(data.mode);
  const [memoId, setMemoId] = useState(data.memoId);
  const selectedMemo = memo.find((item) => item.id === data.memoId);
  const [stroke, setStroke] = useState(selectedMemo?.stroke ?? []);
  const [widthInput, setWidthInput] = useState(() => property?.find((item) => item.type === 'width'));
  const [lengthInput, setLengthInput] = useState(() => property?.find((item) => item.type === 'length'));
  const [memoCanvas, setMemoCanvas] = useState(selectedMemo?.canvas);
  const initializedInputRef = useRef('');
  useEffect(() => {
    if (!annotation) return;
    const inputKey = JSON.stringify([data.workKey, data.sheetId, annotation.id, data.mode, data.memoId]);
    if (initializedInputRef.current === inputKey) return;
    initializedInputRef.current = inputKey;
    const selectedMemo = annotation.memo.find((item) => item.id === data.memoId);
    setMode(data.mode);
    setMemoId(data.memoId);
    setStroke(selectedMemo?.stroke ?? []);
    setMemoCanvas(selectedMemo?.canvas);
    setWidthInput(annotation.property?.find((item) => item.type === 'width'));
    setLengthInput(annotation.property?.find((item) => item.type === 'length'));
  }, [annotation, data.workKey, data.sheetId, data.mode, data.memoId]);

  const handleCancel = () => {
    if (!isPending) dispatch(modalActions.hideModal());
  };
  const handleSave = async (updatedAnnotation: IAnnotation | null, keepOpen = false) => {
    if (!annotation || isPending) return;
    setErrorMessage('');
    try {
      if (updatedAnnotation) {
        await annotationMutation.mutateAsync({
          workKey: data.workKey,
          annotationKey: data.annotationId,
          property: updatedAnnotation.property ?? [],
          memo: updatedAnnotation.memo,
        });
      } else {
        await removeMutation.mutateAsync({ workKey: data.workKey, annotationKey: data.annotationId });
      }
      // TODO: 요청 중 다른 모달로 전환됐다면 해당 모달을 닫지 않도록 저장을 시작한 모달과 현재 모달의 동일성을 확인한다.
      if (!keepOpen) dispatch(modalActions.hideModal());
    } catch {
      setErrorMessage('기록을 저장하지 못했습니다. 다시 시도해주세요.');
    }
  };
  const handlePropertySave = (property?: IAnnotation['property']) => {
    if (annotation) void handleSave({ ...annotation, property });
  };
  const handleMemoSave = () => {
    if (!memoCanvas || !annotation) return;
    const updatedAt = new Date().toISOString();
    const updatedMemo = memoId
      ? memo.map((item) => (item.id === memoId ? { ...item, canvas: memoCanvas, stroke, updatedAt } : item))
      : [...memo, { id: uuid4(), canvas: memoCanvas, stroke, updatedAt }];
    void handleSave({ ...annotation, memo: updatedMemo });
  };
  const handleMemoClick = (item: IAnnotationMemo) => {
    setMode('memo');
    setMemoId(item.id);
    setStroke(item.stroke);
    setMemoCanvas(item.canvas);
  };
  const handleMemoDelete = (memoId: string) => {
    if (annotation) void handleSave({ ...annotation, memo: memo.filter((item) => item.id !== memoId) }, true);
  };
  const handleDelete = () => void handleSave(null);

  const isCrack = issue?.id === 'ISSUE-01';
  const title = mode === 'delete' ? '기록을 삭제할까요?' : mode === 'memo-list' ? '전체 메모' : (issue?.name ?? '');
  const description =
    mode === 'delete'
      ? '이 기록에 저장된 모든 내용이 함께 삭제됩니다.'
      : mode === 'memo-list'
        ? `저장된 메모 ${memo.length}건`
        : (issue?.description ?? '');

  const handleSaveClick = () => {
    if (mode === 'memo') {
      if (stroke.length === 0) {
        handleCancel();
        return;
      }
      if (memoCanvas) handleMemoSave();
      return;
    }
    if (!isCrack) {
      handlePropertySave();
      return;
    }

    const updatedProperty = [widthInput, lengthInput].filter(
      (item): item is NonNullable<IAnnotation['property']>[number] => !!item && item.stroke.length > 0,
    );
    handlePropertySave(updatedProperty.length > 0 ? updatedProperty : undefined);
  };

  const handleClear = () => {
    setStroke([]);
  };

  const MemoItems = [...memo].reverse().map((item) => (
    <MemoItemContainer key={item.id}>
      <MemoPreviewContainer onClick={() => handleMemoClick(item)}>
        <Handwriting type='read' value={item} theme={ETheme.BLACK} />
      </MemoPreviewContainer>
      <IconTextBlock
        icon='trash'
        text='삭제'
        design={{
          box: { gap: '5px', event: { cursor: 'pointer' } },
          text: { size: 'small', color: color.RED_3 },
          icon: { size: 'small', kind: 'outlined', color: color.RED_3 },
        }}
        click={{ handler: () => handleMemoDelete(item.id) }}
      />
    </MemoItemContainer>
  ));

  if (!annotation) {
    return (
      <Wrapper $mode={mode}>
        {annotationQuery.isLoading ? (
          <LoadingDots />
        ) : (
          <TextBlock text='기록을 찾을 수 없습니다.' design={{ text: { size: 'medium', color: color.GRAY_1 } }} />
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper $mode={mode}>
      <TextBlock text={title} design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }} />
      <Gap height={'5px'} />
      <TextBlock text={description} design={{ text: { size: 'medium', color: color.BLACK_4 } }} />
      <Gap height={size.MARGIN_1} />
      {mode === 'property' && isCrack && (
        <>
          <PropertyFieldContainer>
            <PropertyLabelContainer>
              <TextBlock text='폭(mm)' design={PROPERTY_LABEL_DESIGN} />
              <IconTextBlock
                icon='trash'
                text='지우기'
                design={PROPERTY_CLEAR_DESIGN}
                click={{ handler: () => setWidthInput((input) => input && { ...input, stroke: [] }) }}
              />
            </PropertyLabelContainer>
            <Handwriting
              type='write'
              value={widthInput}
              initialSize={{ width: 3, height: 1 }}
              placeholder='손글씨로 입력하세요.'
              onChange={(value) => setWidthInput({ ...value, type: 'width', unit: 'mm' })}
            />
          </PropertyFieldContainer>
          <Gap height={size.MARGIN_2} />
          <PropertyFieldContainer>
            <PropertyLabelContainer>
              <TextBlock text='길이(mm)' design={PROPERTY_LABEL_DESIGN} />
              <IconTextBlock
                icon='trash'
                text='지우기'
                design={PROPERTY_CLEAR_DESIGN}
                click={{ handler: () => setLengthInput((input) => input && { ...input, stroke: [] }) }}
              />
            </PropertyLabelContainer>
            <Handwriting
              type='write'
              value={lengthInput}
              initialSize={{ width: 3, height: 1 }}
              placeholder='손글씨로 입력하세요.'
              onChange={(value) => setLengthInput({ ...value, type: 'length', unit: 'mm' })}
            />
          </PropertyFieldContainer>
        </>
      )}
      {mode === 'memo' && (
        <Handwriting
          type='write'
          value={memoCanvas ? { canvas: memoCanvas, stroke } : undefined}
          placeholder='손글씨로 입력하세요.'
          onChange={(value) => {
            setStroke(value.stroke);
            setMemoCanvas(value.canvas);
          }}
        />
      )}
      {mode === 'memo-list' && <MemoItemsContainer>{MemoItems}</MemoItemsContainer>}
      {errorMessage && <TextBlock text={errorMessage} design={{ text: { size: 'small', color: color.RED_2 } }} />}
      {mode !== 'memo-list' && <Gap height={size.MARGIN_2} />}
      {mode !== 'memo-list' && (
        <ButtonContainer>
          {mode === 'delete' && (
            <TextButton
              text='취소'
              theme={ETheme.GRAY}
              kind='outlined'
              size='medium'
              shape='pill'
              width='100%'
              disabled={isPending}
              handleClick={handleCancel}
            />
          )}
          {mode === 'memo' && (
            <TextButton
              text='지우기'
              theme={ETheme.CYAN}
              kind='outlined'
              size='medium'
              shape='pill'
              width='100%'
              disabled={isPending}
              handleClick={handleClear}
            />
          )}
          {mode === 'delete' ? (
            <TextButton
              text='삭제'
              theme={ETheme.CYAN}
              size='medium'
              shape='pill'
              width='100%'
              disabled={isPending}
              isLoading={isPending}
              handleClick={handleDelete}
            />
          ) : (
            <TextButton
              text={mode === 'property' ? '물성치 추가' : '메모 추가'}
              theme={ETheme.CYAN}
              size='medium'
              shape='pill'
              width='100%'
              disabled={isPending}
              isLoading={isPending}
              handleClick={handleSaveClick}
            />
          )}
        </ButtonContainer>
      )}
    </Wrapper>
  );
};

export default AnnotationModal;
